import z from 'zod'
import { MeetingLocationType } from '~/api/mutations/action'
import { formatAddress } from '~/domains/common/utils'
import type { AddressResponse } from '~/types/global'
import { isHtmlEmpty } from '~/utils'

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

export const meetingSchema = z
  .object({
    subject: z.string().min(1, 'Le sujet est requis'),
    organizer: z.string().optional(),
    fromDatetime: z.coerce.date({
      error: () => 'Veuillez renseigner une date de début valide'
    }),
    toDatetime: z.coerce.date({
      error: () => 'Veuillez renseigner une date de fin valide'
    }),
    wholeDay: z.boolean().optional(),
    orderId: z.string().nullable().optional(),
    locationType: z.enum(MeetingLocationType, {
      error: () => 'Veuillez renseigner un lieu'
    }),
    contactId: z.coerce.number().optional(),
    // The address autocomplete stores a structured address in the form state;
    // the submitted value comes from locationData, this field only drives validation.
    address: z.custom<AddressResponse>().optional(),
    notes: z.string().optional(),
    participants: z.array(z.string()).optional(),
    isOnline: z.boolean().optional(),
    sendInvitation: z.boolean().optional(),
    invitationTemplateId: z.preprocess(
      (val) =>
        typeof val === 'number' && !Number.isNaN(val) ? val : undefined,
      z.number().optional()
    ),
    invitationSubject: z.string().optional(),
    invitationContent: z.string().optional(),
    alertDaysBefore: z.string().optional()
  })
  .refine(
    (data) =>
      data.locationType !== MeetingLocationType.CONTACT || data.contactId,
    {
      message: 'Le contact est requis',
      path: ['contactId']
    }
  )
  .refine(
    (data) =>
      data.locationType !== MeetingLocationType.CUSTOM ||
      Boolean(formatAddress(data.address)),
    {
      message: "L'adresse est requise",
      path: ['address']
    }
  )
  .refine(
    (data) =>
      data.wholeDay
        ? startOfDay(data.fromDatetime) <= startOfDay(data.toDatetime)
        : data.fromDatetime < data.toDatetime,
    {
      message: 'La date de début doit être antérieure à la date de fin',
      path: ['toDatetime']
    }
  )
  .refine(
    (data) => !data.sendInvitation || Boolean(data.invitationSubject?.trim()),
    {
      message: "Le sujet de l'email est requis",
      path: ['invitationSubject']
    }
  )
  .refine(
    (data) => !data.sendInvitation || !isHtmlEmpty(data.invitationContent),
    {
      message: "Le contenu de l'email est requis",
      path: ['invitationContent']
    }
  )

export type MeetingFormValues = z.infer<typeof meetingSchema>
